import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LifeBuoy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatarTelefone } from '@/utils/formatters';
import { requisitarAtivacao } from '@/hooks/useAtivacaoLinhas';
import { TRIAGEM_BTN, TRIAGEM_BTN_ATIVO, TRIAGEM_TH, TRIAGEM_TD } from '../triagem/triagemTabela';
import { CardIsabellaCiclo } from './CardIsabellaCiclo';

interface Resolvido { razao_social: string; numero_pedido: string; telefone: string | null; atendimento_em: string; validada_em: string; como: 'cliente' | 'na_mao'; horas: number }
interface ResumoAjuda {
  pediram: number; atendidos: number; confirmados: number; na_mao: number; abertos: number;
  horas_primeira_resposta: number | null; horas_confirmar: number | null; horas_aberto_mais_antigo: number | null;
  resolvidos: Resolvido[];
}

const PERIODOS = [{ dias: 1, rotulo: 'Hoje' }, { dias: 7, rotulo: '7 dias' }, { dias: 30, rotulo: 'Mês' }] as const;
const horas = (h: number | null | undefined) => (h == null ? '—' : `${h.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h`);
const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : '');
const quando = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/**
 * O CONTROLE DA AJUDA (Douglas 24/09/2026): quantos pedem ajuda, quantos são
 * atendidos, quantos confirmam que deu certo e em quanto tempo. Conta pelo
 * histórico da linha: quem fecha sai da fila mas continua contado aqui.
 */
export function FaixaAjudaEsim({ segmento, recorteLigado, onRecorte }: { segmento: string; recorteLigado: boolean; onRecorte: () => void }) {
  const [dias, setDias] = useState<number>(30);
  const [verResolvidos, setVerResolvidos] = useState(false);
  const consulta = useQuery({ queryKey: ['triagem-esim-ajuda', segmento, dias],
    queryFn: () => requisitarAtivacao<ResumoAjuda>(`triagem/ajuda?segmento=${segmento}&dias=${dias}`), refetchInterval: 60_000 });
  const r = consulta.data;
  const resolvidos = (r?.confirmados || 0) + (r?.na_mao || 0);

  return <div className="border-b border-border/40 px-3 py-2">
    <div className="mb-1.5 flex items-center gap-2">
      <LifeBuoy className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
      <span className="text-[11px] font-semibold text-primary">Ajuda da Qualidade</span>
      <span className="text-[10px] text-muted-foreground">o relógio começa quando o cliente pede ajuda</span>
      <div className="ml-auto flex gap-1" role="group" aria-label="Período da ajuda">
        {PERIODOS.map(p => <button key={p.dias} type="button" aria-pressed={dias === p.dias} onClick={() => setDias(p.dias)}
          className={`${dias === p.dias ? TRIAGEM_BTN_ATIVO : TRIAGEM_BTN} !w-auto px-2 text-[10.5px]`}>{p.rotulo}</button>)}
      </div>
    </div>
    <div className="grid grid-cols-5 gap-1.5">
      <button type="button" onClick={onRecorte} aria-pressed={recorteLigado} title="Mostrar na lista só quem pediu ajuda"
        className={`rounded-md border px-2.5 py-1.5 text-left ${recorteLigado ? 'border-primary' : 'border-border/60'} hover:border-primary`}>
        <p className="text-[10px] text-muted-foreground">Pediram ajuda {dias === 1 ? 'hoje' : dias === 7 ? 'nos 7 dias' : 'no mês'}</p>
        <p className="font-display text-[16px] font-bold tabular-nums">{r?.pediram ?? '—'}</p>
      </button>
      <div className="rounded-md border border-border/60 px-2.5 py-1.5">
        <p className="text-[10px] text-muted-foreground">Atendidos · 1ª resposta em {horas(r?.horas_primeira_resposta)}</p>
        <p className="font-display text-[16px] font-bold tabular-nums">{r?.atendidos ?? '—'} <span className="text-[11px] font-normal text-muted-foreground">{pct(r?.atendidos || 0, r?.pediram || 0)}</span></p>
      </div>
      <button type="button" onClick={() => setVerResolvidos(true)} title="Ver quem já foi resolvido"
        className="rounded-md border border-border/60 px-2.5 py-1.5 text-left hover:border-[#075E54]">
        <p className="text-[10px] text-[#075E54] dark:text-[#25D366]">Cliente confirmou · em {horas(r?.horas_confirmar)}</p>
        <p className="font-display text-[16px] font-bold tabular-nums text-[#075E54] dark:text-[#25D366]">{r?.confirmados ?? '—'} <span className="text-[11px] font-normal">{pct(r?.confirmados || 0, r?.pediram || 0)}</span></p>
      </button>
      <button type="button" onClick={onRecorte} aria-pressed={recorteLigado} title="Mostrar na lista só quem pediu ajuda"
        className="rounded-md border border-primary/60 bg-primary/5 px-2.5 py-1.5 text-left hover:border-primary">
        <p className="text-[10px] text-primary">Em aberto · mais antigo {horas(r?.horas_aberto_mais_antigo)}</p>
        <p className="font-display text-[16px] font-bold tabular-nums text-primary">{r?.abertos ?? '—'}</p>
      </button>
      <CardIsabellaCiclo etapa="ativacao_esim" segmento={segmento} dias={dias} />
    </div>
    <p className="mt-1 text-[10px] text-muted-foreground">Validado na mão, sem o 2 do cliente: {r?.na_mao ?? 0} ·{' '}
      <button type="button" className="underline hover:text-primary" onClick={() => setVerResolvidos(true)}>ver resolvidos ({resolvidos})</button></p>

    <Dialog open={verResolvidos} onOpenChange={setVerResolvidos}><DialogContent className="max-w-3xl">
      <DialogHeader><DialogTitle>Resolvidos · {PERIODOS.find(p => p.dias === dias)?.rotulo}</DialogTitle></DialogHeader>
      <div className="max-h-[60vh] overflow-auto">
        <table className="w-full table-fixed"><thead className="sticky top-0"><tr>
          <th className={TRIAGEM_TH}>Cliente · pedido</th><th className={`${TRIAGEM_TH} w-[120px]`}>Linha</th>
          <th className={`${TRIAGEM_TH} w-[100px]`}>Pediu ajuda</th><th className={`${TRIAGEM_TH} w-[100px]`}>Fechou</th>
          <th className={`${TRIAGEM_TH} w-[70px]`}>Tempo</th><th className={`${TRIAGEM_TH} w-[110px]`}>Como</th>
        </tr></thead><tbody>
          {(r?.resolvidos || []).map((x, i) => <tr key={i} className="border-t border-border/40">
            <td className={TRIAGEM_TD}><span className="block truncate text-[11px] font-semibold">{x.razao_social}</span><span className="block text-[9px] text-muted-foreground">{x.numero_pedido}</span></td>
            <td className={`${TRIAGEM_TD} text-[11px]`}>{formatarTelefone(x.telefone || '') || '—'}</td>
            <td className={`${TRIAGEM_TD} text-[11px]`}>{quando(x.atendimento_em)}</td>
            <td className={`${TRIAGEM_TD} text-[11px]`}>{quando(x.validada_em)}</td>
            <td className={`${TRIAGEM_TD} text-[11px] tabular-nums`}>{horas(x.horas)}</td>
            <td className={`${TRIAGEM_TD} text-[11px] ${x.como === 'cliente' ? 'text-[#075E54] dark:text-[#25D366]' : 'text-muted-foreground'}`}>{x.como === 'cliente' ? 'Cliente digitou 2' : 'Validado na mão'}</td>
          </tr>)}
          {!r?.resolvidos?.length && <tr><td colSpan={6} className="p-4 text-center text-[11px] text-muted-foreground">Nenhum caso de ajuda resolvido neste período.</td></tr>}
        </tbody></table>
      </div>
    </DialogContent></Dialog>
  </div>;
}
